import {
  AppCard,
  Button,
  Form,
  FormAsyncAutocomplete,
  FormCheckbox,
  FormTextField,
  Icon,
  InfoField,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiClose, mdiContentSaveOutline } from "@mdi/js";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { Vendor } from "../../../../@types/services/Vendor";
import { listSegments } from "../../../../__generated__/orval/endpoints/segments/segments";
import { listStates } from "../../../../__generated__/orval/endpoints/states/states";
import { ForbiddenError } from "../../../../errors/ForbiddenError";
import { useAuth } from "../../../../hooks/useAuth";
import {
  type EditVendorFormInput,
  type EditVendorFormSchema,
  editVendorFormSchema,
} from "../../../../schemas/vendorEditSchema";
import { updateVendor } from "../../../../services/vendors";
import { Route as VendorRoute } from "./route";

const isBrazilianCountry = (country?: string): boolean =>
  country?.trim() === "Brazil";

const contactFields: Array<keyof EditVendorFormSchema> = [
  "accountmanageremailaddress",
  "accountmanagername",
  "accountmanagertelephone",
  "accountsreceivableemailaddress",
  "accountsreceivablename",
  "accountsreceivabletelephone",
  "fax",
  "hseemailaddress",
  "hsename",
  "hsetelephone",
  "phone",
  "website",
];

const preserveClearedContactFields = (
  payload: Partial<EditVendorFormSchema>,
): void => {
  contactFields.forEach((fieldName) => {
    if (payload[fieldName] === undefined) {
      payload[fieldName] = "" as never;
    }
  });
};

const normalizeVendorPayload = (
  data: EditVendorFormSchema,
  canEditTaxNumber: boolean,
): Partial<EditVendorFormSchema> => {
  const isBrazil = isBrazilianCountry(data.country);
  const payload: Partial<EditVendorFormSchema> = {
    ...data,
    inscricao_estadual: isBrazil ? data.inscricao_estadual : undefined,
    inscricao_municipal: isBrazil ? data.inscricao_municipal : undefined,
    state_id: isBrazil ? data.state_id : undefined,
    state_old: isBrazil ? undefined : data.state_old,
  };

  if (!canEditTaxNumber) {
    delete payload.taxnumber1;
  }

  preserveClearedContactFields(payload);

  return payload;
};

const mapVendorToDefaultValues = (vendor: Vendor): EditVendorFormInput => {
  return {
    accountmanageremailaddress: vendor.accountmanageremailaddress ?? "",

    accountmanagername: vendor.accountmanagername ?? "",
    accountmanagertelephone: vendor.accountmanagertelephone ?? "",
    accountsreceivableemailaddress: vendor.accountsreceivableemailaddress ?? "",

    accountsreceivablename: vendor.accountsreceivablename ?? "",
    accountsreceivabletelephone: vendor.accountsreceivabletelephone ?? "",
    address_number: vendor.address_number ?? "",
    address_suppl: vendor.address_suppl ?? "",

    address1: vendor.address1 ?? "",
    address2: vendor.address2 ?? "",

    areyouastate_governmentagency:
      vendor.areyouastate_governmentagency ?? false,
    city: vendor.city ?? "",
    country: vendor.country ?? "",
    descriptionofservices: vendor.descriptionofservices ?? "",
    district: vendor.district ?? "",
    email: vendor.email ?? "",
    fax: vendor.fax ?? "",
    fullvendorname: vendor.fullvendorname ?? "",
    hseemailaddress: vendor.hseemailaddress ?? "",

    hsename: vendor.hsename ?? "",
    hsetelephone: vendor.hsetelephone ?? "",
    inscricao_estadual: vendor.inscricao_estadual ?? "",
    inscricao_municipal: vendor.inscricao_municipal ?? "",

    phone: vendor.phone ?? "",
    pobox: vendor.pobox ?? "",
    popostalcode: vendor.popostalcode ?? "",
    postalcode: vendor.postalcode ?? "",
    segment: vendor.segment ?? "",
    segment2: vendor.segment2 ?? "",
    state_id: vendor.state_id
      ? { id: Number(vendor.state_id), name: vendor.state_name ?? "" }
      : null,
    state_old: vendor.state_old ?? "",

    taxnumber1: vendor.taxnumber1 ?? "",
    website: vendor.website ?? "",
  };
};

const RouteComponent: React.FC = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const vendor = VendorRoute.useLoaderData();
  const canEditTaxNumber = Boolean(user?.is_admin || user?.is_superuser);

  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { isDirty },
    reset,
    watch,
  } = useForm({
    defaultValues: mapVendorToDefaultValues(vendor),
    resolver: zodResolver(editVendorFormSchema),
  });

  useEffect(() => {
    reset(mapVendorToDefaultValues(vendor));
  }, [reset, vendor]);

  const country = watch("country");
  const isBrazil = isBrazilianCountry(country);

  const onSubmit = useCallback(
    async (data: EditVendorFormSchema) => {
      try {
        setSubmitting(true);
        const updatedVendor = await updateVendor(
          vendor.id,
          normalizeVendorPayload(data, canEditTaxNumber),
        );

        reset(mapVendorToDefaultValues(updatedVendor));
        queryClient.setQueryData(["vendor", vendor.id], updatedVendor);

        await queryClient.invalidateQueries({
          queryKey: ["vendor", vendor.id],
        });
        await router.invalidate();

        enqueueSnackbar(
          t("vsupplierregistration.vendors.edit.messages.success") ||
            "Vendor updated successfully",
          { variant: "success" },
        );

        await router.navigate({ to: "/vsupplierregistration/vendor" });
        await router.invalidate();
      } catch (err) {
        enqueueSnackbar(
          t("vsupplierregistration.vendors.edit.messages.error") ||
            "Error updating vendor",
          { variant: "error" },
        );
        console.error("Error updating vendor:", err);
      } finally {
        setSubmitting(false);
      }
    },
    [
      canEditTaxNumber,
      enqueueSnackbar,
      queryClient,
      reset,
      router,
      t,
      vendor.id,
    ],
  );

  const handleCancelClick = useCallback(() => {
    if (isDirty) {
      setConfirmOpen(true);
    } else {
      router.navigate({ to: "/vsupplierregistration/vendor" });
    }
  }, [isDirty, router.navigate]);

  const handleConfirmCancel = useCallback(() => {
    setConfirmOpen(false);
    enqueueSnackbar(
      t("vsupplierregistration.vendors.edit.messages.cancelled") ||
        "Edit cancelled",
      { variant: "info" },
    );
    router.navigate({ to: "/vsupplierregistration/vendor" });
  }, [enqueueSnackbar, router.navigate, t]);

  const handleCloseDialog = useCallback(() => {
    setConfirmOpen(false);
  }, []);

  return (
    <Box sx={{ margin: "0 auto", maxWidth: "100%", padding: 3 }}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={4}>
          {/* Basic Information */}
          <AppCard title={t("common.basicInformation")}>
            <Grid container spacing={3}>
              <Grid size={{ md: 3, xs: 12 }}>
                <FormTextField
                  control={control}
                  label={t("common.companyName")}
                  name="fullvendorname"
                  required
                />
              </Grid>
              <Grid size={{ md: 3, xs: 12 }}>
                <InfoField label={t("common.email")} text={vendor.email} />
              </Grid>
              <Grid size={{ md: 3, xs: 12 }}>
                <FormAsyncAutocomplete
                  control={control}
                  fetchOptions={listSegments}
                  filters={(searchTerm) => ({
                    name: searchTerm,
                  })}
                  getOptionLabel={(option) => option.name}
                  label={t("common.mainSegment")}
                  name="segment"
                  queryKey={["segments", "infinite"]}
                  required
                />
              </Grid>
              <Grid size={{ md: 3, xs: 12 }}>
                <FormAsyncAutocomplete
                  control={control}
                  fetchOptions={listSegments}
                  filters={(searchTerm) => ({
                    name: searchTerm,
                  })}
                  getOptionLabel={(option) => option.name}
                  label={t("common.secondarySegment")}
                  name="segment2"
                  queryKey={["segments", "infinite"]}
                />
              </Grid>
              <Grid size={12}>
                <FormTextField
                  control={control}
                  label={t("common.serviceDescription")}
                  maxRows={10}
                  minRows={4}
                  multiline
                  name="descriptionofservices"
                />
              </Grid>
            </Grid>
          </AppCard>

          {/* Address */}
          <AppCard title={t("common.addressInformation")}>
            <Grid container spacing={3}>
              <Grid size={12}>
                <FormTextField
                  control={control}
                  label={t("common.address")}
                  name="address1"
                  required
                />
              </Grid>
              <Grid size={{ md: 4, xs: 12 }}>
                <FormTextField
                  control={control}
                  label={t("common.supplement")}
                  name="address_suppl"
                />
              </Grid>
              <Grid size={{ md: 2, xs: 12 }}>
                <FormTextField
                  control={control}
                  label={t("common.number")}
                  name="address_number"
                  type="number"
                />
              </Grid>
              <Grid size={{ md: 2, xs: 12 }}>
                {/* TODO: Convert to <FormAsyncAutocomplete /> */}
                <FormTextField
                  control={control}
                  label={t("common.country")}
                  name="country"
                  required
                />
              </Grid>
              <Grid size={{ md: 2, xs: 12 }}>
                {isBrazil ? (
                  <FormAsyncAutocomplete
                    control={control}
                    fetchOptions={listStates}
                    filters={(searchTerm) => ({
                      name: searchTerm,
                    })}
                    getOptionLabel={(option) => option.name}
                    label={t("common.state")}
                    name="state_id"
                    queryKey={["states", "infinite"]}
                  />
                ) : (
                  <FormTextField
                    control={control}
                    label={t("common.state")}
                    name="state_old"
                  />
                )}
              </Grid>
              <Grid size={{ md: 2, xs: 12 }}>
                <FormTextField
                  control={control}
                  label={t("common.city")}
                  name="city"
                  required
                />
              </Grid>
              <Grid size={{ md: 3, xs: 12 }}>
                <FormTextField
                  control={control}
                  label={t("common.district")}
                  name="district"
                />
              </Grid>
              <Grid size={{ md: 3, xs: 12 }}>
                <FormTextField
                  control={control}
                  label={t("common.postalCode")}
                  name="postalcode"
                />
              </Grid>
              <Grid size={{ md: 3, xs: 12 }}>
                <FormTextField
                  control={control}
                  label={t("common.postalBox")}
                  name="pobox"
                />
              </Grid>
              <Grid size={{ md: 3, xs: 12 }}>
                <FormTextField
                  control={control}
                  label={t("common.postalBoxCode")}
                  name="popostalcode"
                />
              </Grid>
            </Grid>
          </AppCard>

          {/* Government Information */}
          <AppCard title={t("common.governmentInformation")}>
            <Grid container spacing={3}>
              <Grid size={12}>
                <FormCheckbox
                  control={control}
                  label={t("common.isStateAgency")}
                  name="areyouastate_governmentagency"
                />

                {/* <Controller
                  control={control}
                  name="areyouastate_governmentagency"
                  render={({ field }) => (
                    <FormControl component="fieldset">
                      <FormLabel component="legend">
                        {t(
                          "common.isStateAgency",
                        )}
                      </FormLabel>
                      <RadioGroup
                        {...field}
                        onChange={(e) => {
                          const { value } = e.target;
                          field.onChange(
                            value === "" ? null : value === "true",
                          );
                        }}
                        row
                        value={
                          field.value === null ? "" : field?.value?.toString()
                        }
                      >
                        <FormControlLabel
                          control={<Radio />}
                          label={t(
                            "common.yes",
                          )}
                          value="true"
                        />
                        <FormControlLabel
                          control={<Radio />}
                          label={t(
                            "common.no",
                          )}
                          value="false"
                        />
                      </RadioGroup>
                    </FormControl>
                  )}
                /> */}
              </Grid>
              <Grid size={{ md: 4, xs: 12 }}>
                <FormTextField
                  control={control}
                  disabled={!canEditTaxNumber}
                  label={t("common.taxNumber")}
                  name="taxnumber1"
                  required
                  slotProps={{
                    htmlInput: {
                      pattern: isBrazil
                        ? "[A-Z0-9]{12}[0-9]{2}|[A-Z0-9]{2}.[A-Z0-9]{3}.[A-Z0-9]{3}/[A-Z0-9]{4}-[0-9]{2}"
                        : undefined,
                    },
                  }}
                />
              </Grid>
              {isBrazil && (
                <>
                  <Grid size={{ md: 4, xs: 12 }}>
                    <FormTextField
                      control={control}
                      label={t("common.stateRegistration")}
                      name="inscricao_estadual"
                    />
                  </Grid>
                  <Grid size={{ md: 4, xs: 12 }}>
                    <FormTextField
                      control={control}
                      label={t("common.municipalRegistration")}
                      name="inscricao_municipal"
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </AppCard>

          {/* Contact Information */}
          <AppCard title={t("common.contactInformation")}>
            <Grid container spacing={3}>
              <Grid size={{ md: 6, xs: 12 }}>
                <FormTextField
                  control={control}
                  label={t("common.website")}
                  name="website"
                />
              </Grid>
              <Grid size={{ md: 3, xs: 12 }}>
                <FormTextField
                  control={control}
                  label={t("common.phone")}
                  name="phone"
                />
              </Grid>
              <Grid size={{ md: 3, xs: 12 }}>
                <FormTextField
                  control={control}
                  label={t("common.fax")}
                  name="fax"
                />
              </Grid>
            </Grid>
          </AppCard>

          {/* Account Receivable */}
          <AppCard title={t("common.accountsReceivable")}>
            <Grid container spacing={3}>
              <Grid size={{ md: 4, xs: 12 }}>
                <FormTextField
                  control={control}
                  label={t("common.name")}
                  name="accountsreceivablename"
                />
              </Grid>
              <Grid size={{ md: 4, xs: 12 }}>
                <FormTextField
                  control={control}
                  label={t("common.email")}
                  name="accountsreceivableemailaddress"
                  type="email"
                />
              </Grid>
              <Grid size={{ md: 4, xs: 12 }}>
                <FormTextField
                  control={control}
                  label={t("common.phone")}
                  name="accountsreceivabletelephone"
                />
              </Grid>
            </Grid>
          </AppCard>

          {/* HSE */}
          <AppCard title={t("common.hse")}>
            <Grid container spacing={3}>
              <Grid size={{ md: 4, xs: 12 }}>
                <FormTextField
                  control={control}
                  label={t("common.name")}
                  name="hsename"
                />
              </Grid>
              <Grid size={{ md: 4, xs: 12 }}>
                <FormTextField
                  control={control}
                  label={t("common.email")}
                  name="hseemailaddress"
                  type="email"
                />
              </Grid>
              <Grid size={{ md: 4, xs: 12 }}>
                <FormTextField
                  control={control}
                  label={t("common.phone")}
                  name="hsetelephone"
                />
              </Grid>
            </Grid>
          </AppCard>

          {/* Account Manager */}
          <AppCard title={t("common.accountManager")}>
            <Grid container spacing={3}>
              <Grid size={{ md: 4, xs: 12 }}>
                <FormTextField
                  control={control}
                  label={t("common.name")}
                  name="accountmanagername"
                />
              </Grid>
              <Grid size={{ md: 4, xs: 12 }}>
                <FormTextField
                  control={control}
                  label={t("common.email")}
                  name="accountmanageremailaddress"
                  type="email"
                />
              </Grid>
              <Grid size={{ md: 4, xs: 12 }}>
                <FormTextField
                  control={control}
                  label={t("common.phone")}
                  name="accountmanagertelephone"
                />
              </Grid>
            </Grid>
          </AppCard>

          {/* Actions */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "flex-end",
              mt: 4,
            }}
          >
            <Button
              color="error"
              disabled={submitting}
              onClick={handleCancelClick}
              startIcon={<Icon icon={mdiClose} />}
              variant="outlined"
            >
              {t("common.cancel")}
            </Button>
            <Button
              color="success"
              disabled={submitting}
              loading={submitting}
              startIcon={<Icon icon={mdiContentSaveOutline} />}
              type="submit"
            >
              {t("common.save")}
            </Button>
          </Box>
        </Stack>
      </Form>
      {/* Confirmation Dialog */}
      <Dialog
        fullWidth
        maxWidth="xs"
        onClose={handleCloseDialog}
        open={confirmOpen}
      >
        <DialogTitle>
          {t("vsupplierregistration.vendors.edit.messages.unsavedConfirmTitle")}
        </DialogTitle>
        <DialogContent dividers>
          <Typography>
            {t("vsupplierregistration.vendors.edit.messages.unsavedConfirm")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            color="inherit"
            disabled={submitting}
            loading={submitting}
            onClick={handleCloseDialog}
            variant="text"
          >
            {t("common.back")}
          </Button>
          <Button
            color="error"
            disabled={submitting}
            loading={submitting}
            onClick={handleConfirmCancel}
            variant="contained"
          >
            {t("common.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export const Route = createFileRoute(
  "/_private/vsupplierregistration/vendor/edit",
)({
  beforeLoad({ context }) {
    const user = context.auth.user;

    if (user?.admin_of_vendor) {
      return;
    }

    throw new ForbiddenError();
  },
  component: RouteComponent,
});
